import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { CurrentCarrierComparison } from '@entities/product/product.model';
import {
  currentCarrierBiLimitDisplayNames,
  currentCarrierLapseInCoverage,
} from '@shared/services/current-carrier-metadata.service';
import { Nullable } from '@shared/utils/type.utils';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'nwx-current-carrier-comparison-form',
  templateUrl: './current-carrier-comparison-form.component.html',
  styleUrl: './current-carrier-comparison-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CurrentCarrierComparisonFormComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() currentCarrierComparison!: Nullable<CurrentCarrierComparison>;
  @Input() highlightAcknowledgementRequired = false;

  @Output() formReady = new EventEmitter<FormGroup>();

  form = new FormGroup<{ [key: string]: AbstractControl<string> }>({
    acknowledgement: new FormControl('', {
      nonNullable: true,
      validators: [(control) => this.validateAcknowledgement(control)],
    }),
  });
  showAcknowledgementHighlight = false;

  private unsubscribe$ = new Subject<void>();

  constructor(private changeDetector: ChangeDetectorRef) {
    this.form
      .get('acknowledgement')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((value) => {
        this.chooseShowAcknowledgementHighlight();
      });
  }

  ngOnInit(): void {
    this.formReady.emit(this.form);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.highlightAcknowledgementRequired) {
      this.chooseShowAcknowledgementHighlight();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getLapseVerbiage(key: string): string {
    return (
      currentCarrierLapseInCoverage.find((lapse) => lapse.key === key)?.value ||
      key
    );
  }

  getBILimitVerbiage(key: string): string {
    return (
      currentCarrierBiLimitDisplayNames.find((limit) => limit.key === key)
        ?.value || key
    );
  }

  private chooseShowAcknowledgementHighlight(): void {
    let newValue: boolean = this.showAcknowledgementHighlight;
    if (this.highlightAcknowledgementRequired) {
      const val = this.form.get('acknowledgement')?.value;
      newValue = val === '' || val === null || val === undefined;
    }
    if (newValue !== this.showAcknowledgementHighlight) {
      this.showAcknowledgementHighlight = newValue;
      this.changeDetector.markForCheck();
    }
  }

  private validateAcknowledgement(
    control: AbstractControl
  ): ValidationErrors | null {
    if (!control.value) {
      return { fullVerbiageProvided: '' };
    }
    return null;
  }
}
